/* Creme Castle — stale-cart guard (Step 2, Sept 2026).
 *
 * The cart that sits for hours: a slot chosen at 10 AM is still in the cart at
 * 6 PM. This guard re-runs the slot engine for every cake line whenever the
 * cart is shown (drawer open, cart page) and again when "Checkout" is pressed.
 * A line whose date + slot is no longer offerable is marked stale, gets an
 * inline re-pick, and checkout is blocked until it is fixed.
 *
 * Only lines written by the new picker carry properties[_rule]; older lines
 * (Day = "Today") cannot be judged here and are left to the OMS SLOT-CHECK.
 * Fails open: any error in this file lets checkout proceed.
 *
 * 4 Oct 2026 (order #187289): an add-on line whose cake is no longer in the
 * cart kept a week-old Day/Time and was never judged, because add-on lines
 * were left to syncAddons() to overwrite. cakeLines() now also judges such
 * "orphan" add-ons on their own rule.
 */
(function () {
  "use strict";
  var E = window.CCSlotEngine, D = window.ccSlotData;
  if (!E || !D || !window.ccClock) return;

  var lastStale = [];   // [{line, props, plan, cityName}]
  var busy = false;

  function cakeLines(cart) {
    var out = [];
    (cart.items || []).forEach(function (it, i) {
      var p = it.properties || {};
      // An add-on normally rides on its cake's slot (syncAddons overwrites it). When no
      // cake is left in the cart it keeps whatever Day/Time it was given — possibly days
      // old — so it must be judged on its own rule like any other line.
      var orphanAddon = followsCake(p) && !!p.Day && !cakeFor(cart, p);
      if (p._cake !== "yes" && !(p.city && !p._for) && !orphanAddon) return;
      if (!p._rule) return; // legacy line, not ours to judge
      out.push({ line: i + 1, item: it, props: p });
    });
    return out;
  }

  var SLOT_KEYS = ["Day", "Time", "city", "Area/Sector", "Delivery", "Delivery Day"];
  var lastNeeds = [];   // lines with no slot and no cake to copy from
  function isIso(d) { return /^\d{4}-\d{2}-\d{2}$/.test(d || ""); }

  // The cake this add-on belongs to: same title as _for, else the earliest-dated cake in the cart.
  function cakeFor(cart, p) {
    var cakes = (cart.items || []).filter(function (it) {
      var q = it.properties || {}; return q._cake === "yes" && isIso(q.Day) && q.Time;
    });
    if (!cakes.length) return null;
    for (var i = 0; i < cakes.length; i++) if (cakes[i].product_title === p._for) return cakes[i];
    return cakes.sort(function (a, b) { return a.properties.Day < b.properties.Day ? -1 : 1; })[0];
  }

  // Lines that must ride with a cake: dessert-box / add-on lines, plus any non-cake line with no Day.
  function followsCake(p) { return p._cake !== "yes" && (p._addon === "yes" || !!p._for || !p.Day); }

  // Copy the cake's slot onto ONE mismatched add-on line; returns a promise of the new cart, or null.
  function syncOnce(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (!followsCake(p)) continue;
      var cake = cakeFor(cart, p); if (!cake) continue;
      var c = cake.properties;
      var same = SLOT_KEYS.every(function (k) { return (c[k] || "") === (p[k] || ""); });
      if (same) continue;
      var props = {}; for (var k in p) props[k] = p[k];
      SLOT_KEYS.forEach(function (k) { if (c[k]) props[k] = c[k]; else delete props[k]; });
      props._for = cake.product_title;
      return fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ line: i + 1, properties: props }) })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
    }
    return null;
  }

  // One line per request (line numbers can shift after a property change), capped at 10 rounds.
  function syncAddons(cart, n, changed) {
    var next = (n || 0) < 10 ? syncOnce(cart) : null;
    if (!next) return Promise.resolve({ cart: cart, changed: !!changed });
    return next.then(function (c) { return syncAddons(c, (n || 0) + 1, true); });
  }

  // After syncing, anything still without a Day has no cake to follow -> ask the customer.
  function needsSlot(cart) {
    var out = [];
    (cart.items || []).forEach(function (it, i) {
      var p = it.properties || {};
      if (p.Day) return;                                   // has a slot (new or legacy)
      var rule = D.rules[p._rule] || D.rules.designer;      // no rule -> designer, as elsewhere
      out.push({ line: i + 1, item: it, props: p, rule: rule, ruleHandle: p._rule || "designer" });
    });
    return out;
  }

  function check() {
    return window.ccClock.sync().then(function () {
      return fetch("/cart.js", { cache: "no-store", credentials: "same-origin" }).then(function (r) { return r.json(); });
    }).then(function (cart) { return syncAddons(cart); })
    .then(function (res) {
      var cart = res.cart;
      // Reloading to show the synced lines. Return a blocker, not an empty list: a Checkout
      // click is what may have triggered this sync, and an empty list would open Shopflo
      // mid-reload with the cart's stale/slotless lines never judged.
      if (res.changed && /^\/cart\/?$/.test(location.pathname)) { window.location.reload(); return [{ reloading: true }]; }
      var now = window.ccClock.now();
      lastStale = [];
      cakeLines(cart).forEach(function (l) {
        var rule = D.rules[l.props._rule], city = D.cities[l.props.city];
        if (!rule || !city) return;
        var ok = E.isOfferable(rule, city, l.props.Day, l.props.Time, now);
        if (!ok) lastStale.push({ line: l.line, item: l.item, props: l.props, rule: rule, city: city, plan: E.plan(rule, city, now) });
      });
      lastNeeds = needsSlot(cart);
      paint();
      return lastStale.concat(lastNeeds);
    }).catch(function (e) { console.warn("[cc-guard] check failed, allowing", e); lastStale = []; lastNeeds = []; return []; });
  }

  /* ---------- UI ---------- */
  var CSS = ".cc-stale{margin:10px 0 4px;padding:12px 14px;border:1.5px solid #DB5436;border-radius:10px;background:#fff6f3;font-family:Montserrat,sans-serif;font-size:13px;color:#3C0618}" +
    ".cc-stale b{display:block;margin-bottom:6px}.cc-stale .cc-days{display:flex;gap:6px;flex-wrap:wrap;margin:6px 0}.cc-stale .cc-day,.cc-stale .cc-pill{border:1px solid #cdcdcd;border-radius:6px;padding:6px 10px;background:#fff;cursor:pointer;font-size:12.5px}" +
    ".cc-stale .cc-day.on,.cc-stale .cc-pill.on{background:#f26d79;color:#fff;border-color:#f26d79}.cc-stale .cc-pills{display:flex;gap:6px;flex-wrap:wrap;margin:6px 0}.cc-stale input[type=date]{margin:4px 0;padding:6px 8px;border:1px solid #cdcdcd;border-radius:6px}" +
    ".cc-stale .cc-save{margin-top:8px;background:#DB5436;color:#fff;border:none;border-radius:8px;padding:9px 14px;font-weight:600;cursor:pointer}.cc-stale .cc-save[disabled]{opacity:.5;cursor:default}.cc-stale .cc-err{color:#b00020;margin-top:6px;font-size:12px}" +
    ".cc-guard-msg{margin:8px 0;padding:10px 12px;border-radius:8px;background:#fff6f3;border:1px solid #DB5436;color:#3C0618;font-size:13px;font-family:Montserrat,sans-serif}" +
    ".cc-stale select{margin:6px 6px 0 0;padding:7px 8px;border:1px solid #cdcdcd;border-radius:6px;font:inherit}";
  function ensureCss() { if (document.getElementById("cc-stale-css")) return; var s = document.createElement("style"); s.id = "cc-stale-css"; s.textContent = CSS; document.head.appendChild(s); }

  // Cart page rows are <div>s; drawer rows are <tr>s, so the box goes in their details cell.
  function lineNodes(line) {
    return Array.prototype.slice.call(document.querySelectorAll("#CartDrawer-Item-" + line + ", #CartItem-" + line)).map(function (n) {
      return n.tagName === "TR" ? (n.querySelector(".cart-item__details") || n) : n;
    });
  }

  function paint() {
    ensureCss();
    document.querySelectorAll(".cc-stale, .cc-guard-msg").forEach(function (n) { n.remove(); });
    lastStale.forEach(function (s) {
      lineNodes(s.line).forEach(function (node) { node.appendChild(buildRepick(s)); });
    });
    lastNeeds.forEach(function (s) {
      lineNodes(s.line).forEach(function (node) { node.appendChild(buildAsk(s)); });
    });
  }

  function buildRepick(s) {
    var box = document.createElement("div"); box.className = "cc-stale";
    var oldDay = /^\d{4}-\d{2}-\d{2}$/.test(s.props.Day || "") ? E.labelShort(s.props.Day) : (s.props.Day || "");
    var old = oldDay + (s.props.Time ? " · " + s.props.Time : "");
    box.innerHTML = "<b>Your " + esc(s.props.Time || "") + " slot has passed — pick a new time</b>" +
      "<div style='font-size:12px;color:#7E6B6E'>Was: " + esc(old) + " · " + esc(s.props.city || "") + "</div>" +
      "<div class='cc-days'></div><input type='date' style='display:none'><div class='cc-pills'></div>" +
      "<button type='button' class='cc-save' disabled>Update delivery</button><div class='cc-err'></div>";
    var days = box.querySelector(".cc-days"), pillsEl = box.querySelector(".cc-pills"), dateIn = box.querySelector("input[type=date]"), save = box.querySelector(".cc-save"), err = box.querySelector(".cc-err");
    var choice = { ymd: "", slot: "" };
    function pills(ymd, windows) {
      pillsEl.innerHTML = ""; choice = { ymd: ymd, slot: "" }; save.disabled = true;
      if (!windows.length) { pillsEl.innerHTML = "<span style='font-size:12px'>No slots on that day.</span>"; return; }
      windows.forEach(function (w) {
        var p = document.createElement("span"); p.className = "cc-pill"; p.textContent = w;
        p.onclick = function () { pillsEl.querySelectorAll(".cc-pill").forEach(function (x) { x.classList.remove("on"); }); p.classList.add("on"); choice.slot = w; save.disabled = false; };
        pillsEl.appendChild(p);
      });
    }
    function dayBtn(label, ymd) {
      var b = document.createElement("span"); b.className = "cc-day"; b.textContent = label;
      b.onclick = function () {
        days.querySelectorAll(".cc-day").forEach(function (x) { x.classList.remove("on"); }); b.classList.add("on");
        if (ymd) { dateIn.style.display = "none"; pills(ymd, E.windowsFor(s.rule, s.city, ymd, window.ccClock.now())); }
        else { dateIn.style.display = "block"; dateIn.min = s.plan.calendarMin; dateIn.value = ""; pills("", []); }
      };
      days.appendChild(b); return b;
    }
    var first = null;
    s.plan.days.forEach(function (d) { var b = dayBtn(d.label, d.ymd); if (!first && d.windows.length) first = b; });
    dayBtn("Other date", "");
    dateIn.addEventListener("change", function () { if (dateIn.value) pills(dateIn.value, E.windowsFor(s.rule, s.city, dateIn.value, window.ccClock.now())); });
    if (first) first.onclick();
    save.onclick = function () {
      if (!choice.ymd || !choice.slot) return;
      save.disabled = true; save.textContent = "Updating…"; err.textContent = "";
      var props = {}; for (var k in s.props) props[k] = s.props[k];
      props.Day = choice.ymd; props.Time = choice.slot;
      // Keep the readable twin in step with Day, but only on lines that carry it,
      // so a re-pick never adds a new visible property to a legacy line.
      if (Object.prototype.hasOwnProperty.call(props, "Delivery Day")) props["Delivery Day"] = E.labelShort(choice.ymd);
      fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ line: s.line, properties: props }) })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () { save.disabled = false; save.textContent = "Update delivery"; err.textContent = "Couldn't update. Please try again."; });
    };
    return box;
  }
  // Dessert-only (or add-on-only) line: city + area first, then days/slots from the engine,
  // earliest slot pre-selected. Reuses the .cc-stale styles.
  function buildAsk(s) {
    var box = document.createElement("div"); box.className = "cc-stale";
    var cities = Object.keys(D.cities).filter(function (n) { return D.cities[n].active !== false; });
    box.innerHTML = "<b>When should we deliver your " + esc(s.item.product_title) + "?</b>" +
      "<select class='cc-city'><option value=''>City</option>" +
        cities.map(function (n) { return "<option>" + esc(n) + "</option>"; }).join("") + "</select> " +
      "<select class='cc-area' disabled><option value=''>Area / sector</option></select>" +
      "<div class='cc-days'></div><div class='cc-pills'></div>" +
      "<button type='button' class='cc-save' disabled>Confirm delivery</button><div class='cc-err'></div>";
    var citySel = box.querySelector(".cc-city"), areaSel = box.querySelector(".cc-area"),
        days = box.querySelector(".cc-days"), pillsEl = box.querySelector(".cc-pills"),
        save = box.querySelector(".cc-save"), err = box.querySelector(".cc-err");
    var choice = { city: "", ymd: "", slot: "" };
    function ready() { save.disabled = !(choice.city && areaSel.value && choice.ymd && choice.slot); }
    function pills(ymd, windows, preselect) {
      pillsEl.innerHTML = ""; choice.ymd = ymd; choice.slot = "";
      if (!windows.length) { pillsEl.innerHTML = "<span style='font-size:12px'>No slots on that day.</span>"; ready(); return; }
      windows.forEach(function (w, i) {
        var p = document.createElement("span"); p.className = "cc-pill"; p.textContent = w;
        p.onclick = function () { pillsEl.querySelectorAll(".cc-pill").forEach(function (x) { x.classList.remove("on"); });
          p.classList.add("on"); choice.slot = w; ready(); };
        pillsEl.appendChild(p);
        if (preselect && i === 0) p.onclick();           // earliest slot pre-selected
      });
    }
    citySel.onchange = function () {
      choice.city = citySel.value; days.innerHTML = ""; pillsEl.innerHTML = "";
      var areas = (window.ccCityAreas || {})[choice.city] || [];
      areaSel.innerHTML = "<option value=''>Area / sector</option>" + areas.map(function (a) { return "<option>" + esc(a) + "</option>"; }).join("");
      areaSel.disabled = !choice.city;
      if (!choice.city) return ready();
      var city = D.cities[choice.city], now = window.ccClock.now(), plan = E.plan(s.rule, city, now), first = null;
      plan.days.forEach(function (d) {
        var b = document.createElement("span"); b.className = "cc-day"; b.textContent = d.label;
        b.onclick = function () { days.querySelectorAll(".cc-day").forEach(function (x) { x.classList.remove("on"); });
          b.classList.add("on"); pills(d.ymd, E.windowsFor(s.rule, city, d.ymd, window.ccClock.now()), true); };
        days.appendChild(b); if (!first && d.windows.length) first = b;
      });
      if (first) first.onclick();                        // earliest day pre-selected
    };
    areaSel.onchange = ready;
    save.onclick = function () {
      if (save.disabled) return;
      save.disabled = true; save.textContent = "Saving…"; err.textContent = "";
      var props = {}; for (var k in s.props) props[k] = s.props[k];
      props.Day = choice.ymd; props.Time = choice.slot; props.city = choice.city;
      props["Area/Sector"] = areaSel.value; props["Delivery Day"] = E.labelShort(choice.ymd);
      props.Delivery = s.ruleHandle === "designer" ? "Standard Delivery" : "Fixed Time Delivery";
      props._rule = s.ruleHandle; props._picker = "v2";
      fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ line: s.line, properties: props }) })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () { save.disabled = false; save.textContent = "Confirm delivery"; err.textContent = "Couldn't save. Please try again."; });
    };
    return box;
  }

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }

  /* ---------- triggers ---------- */
  // Cart page.
  if (/^\/cart\/?$/.test(location.pathname)) document.addEventListener("DOMContentLoaded", function () { check(); });
  // Drawer opening (class "active" is toggled on <cart-drawer>).
  var drawer = document.querySelector("cart-drawer");
  if (drawer && window.MutationObserver) {
    new MutationObserver(function () { if (drawer.classList.contains("active")) check(); }).observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }
  // Re-check after Dawn re-renders the cart sections (quantity change, add-on add).
  document.addEventListener("cart:refresh", function () { check(); });

  // Checkout gate: capture-phase click on the Flo buttons, before their inline onclick.
  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest("#checkout2, #checkout2-drawer, [name='checkout2']");
    if (!btn || busy) return;
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    busy = true;
    var label = btn.textContent;
    btn.disabled = true;
    check().then(function (blocking) {
      busy = false; btn.disabled = false;
      if (blocking.length) {
        var msg = document.createElement("div"); msg.className = "cc-guard-msg";
        msg.textContent = (blocking.length === 1 && blocking[0] && blocking[0].reloading)
          ? "Updating your delivery details — please press Checkout again in a moment."
          : lastNeeds.length
          ? "Please choose when we should deliver your order (above) before checkout."
          : (lastStale.length === 1 ? "One cake's delivery slot has passed. Please pick a new time above before checkout."
                                    : lastStale.length + " cakes have delivery slots that have passed. Please pick new times above before checkout.");
        btn.parentNode.insertBefore(msg, btn);
        var firstBox = document.querySelector(".cc-stale"); if (firstBox) firstBox.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (typeof window.handleFloCheckoutBtn === "function") window.handleFloCheckoutBtn(); else window.location.href = "/checkout";
    });
  }, true);

  window.ccGuard = { check: check };
})();
